/**
 * Gamma Vacuum Product Configurator
 * 
 * Reads configuration options from product metafields and switches between variants.
 * 
 * Updated for high-variant products (250+ variants):
 * - Uses Section Rendering API to fetch variants on-demand
 * - Caches fetched variants to minimize API calls
 * - No longer relies on preloading all product.variants
 * 
 * Integrates with Dawn theme's pub/sub system for price/media updates.
 */

class GammaConfigurator extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.variantLabels = {};
    this.optionValueIds = {};  // Part Number → Shopify option_value_id
    this.selections = {};
    this.currentVariant = null;
    this.variantCache = {};    // Cache fetched variants by Part Number
    this.specsCache = {};      // Cache fetched specifications by Part Number
    this.isSplitProduct = false;
    this.partNumberMap = {};
    this.sectionId = null;
    this.productUrl = null;
    this.productHandle = null;
    this.isFetching = false;
  }

  connectedCallback() {
    this.parseData();
    if (this.options.length > 0) {
      this.render();
      this.selectInitialVariant();
      this.storeDefaultSelections();
      this.attachListeners();
    }
  }

  parseData() {
    try {
      this.options = JSON.parse(this.dataset.options || '[]');
      this.variantLabels = JSON.parse(this.dataset.labels || '{}');
      this.optionValueIds = JSON.parse(this.dataset.optionValueIds || '{}');
      this.isSplitProduct = this.dataset.isSplit === 'true';
      this.partNumberMap = JSON.parse(this.dataset.partMap || '{}');
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.productHandle = this.dataset.productHandle;

      // Load initial variant from JSON script (avoids needing all variants preloaded)
      const initialVariantScript = document.querySelector('[data-gamma-initial-variant]');
      if (initialVariantScript) {
        try {
          const initialVariant = JSON.parse(initialVariantScript.textContent);
          if (initialVariant.id && initialVariant.sku) {
            // Cache the initial variant by its SKU (Part Number)
            this.variantCache[initialVariant.sku] = initialVariant;
            this.currentVariant = initialVariant;

            // Also cache initial specifications
            const initialSpecsScript = document.querySelector('[data-gamma-current-specs]');
            if (initialSpecsScript) {
              try {
                const initialSpecs = JSON.parse(initialSpecsScript.textContent);
                if (initialSpecs && Object.keys(initialSpecs).length > 0) {
                  this.specsCache[initialVariant.sku] = initialSpecs;
                }
              } catch (e) {
                console.warn('GammaConfigurator: Could not parse initial specs', e);
              }
            }
          }
        } catch (e) {
          console.warn('GammaConfigurator: Could not parse initial variant', e);
        }
      }
    } catch (e) {
      console.error('GammaConfigurator: Failed to parse data', e);
    }
  }

  render() {
    const container = this.querySelector('.gamma-configurator__options');
    if (!container) return;

    container.innerHTML = '';
    this.options.forEach((option, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'gamma-configurator__option product-form__input product-form__input--dropdown';
      wrapper.innerHTML = `
        <label class="form__label" for="gamma-option-${this.sectionId}-${index}">
          ${option.name}
        </label>
        <div class="select">
          <select 
            class="select__select" 
            id="gamma-option-${this.sectionId}-${index}" 
            data-option-name="${option.name}"
            data-option-index="${index}"
          >
            ${option.values.map(value => `<option value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</option>`).join('')}
          </select>
          <span class="svg-wrapper">
            <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
            </svg>
          </span>
        </div>
      `;

      const select = wrapper.querySelector('select');
      select.addEventListener('change', () => this.onSelectionChange());
      container.appendChild(wrapper);
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    // Also escape double quotes for use in HTML attributes
    return div.innerHTML.replace(/"/g, '&quot;');
  }

  escapeCssSelector(text) {
    // Escape special characters for use in CSS selectors
    // This handles quotes, backslashes, and other special chars
    return text.replace(/["\\]/g, '\\$&');
  }

  onSelectionChange() {
    // Build current selections from all dropdowns
    this.options.forEach(option => {
      const select = this.querySelector(`[data-option-name="${option.name}"]`);
      if (select) {
        this.selections[option.name] = select.value;
      }
    });

    this.updateDisabledOptions();
    this.switchVariant();
  }

  updateDisabledOptions() {
    // For each option, disable values that have no valid variant combination
    this.options.forEach(option => {
      const select = this.querySelector(`[data-option-name="${option.name}"]`);
      if (!select) return;

      option.values.forEach(value => {
        const testSelection = { ...this.selections, [option.name]: value };
        const hasValidVariant = this.findVariantPartNumber(testSelection) !== null;

        const optionEl = select.querySelector(`option[value="${this.escapeCssSelector(value)}"]`);
        if (optionEl) {
          optionEl.disabled = !hasValidVariant;
          // Update text to show unavailable status
          if (!hasValidVariant) {
            optionEl.textContent = `${value} (unavailable)`;
            optionEl.classList.add('option--unavailable');
          } else {
            optionEl.textContent = value;
            optionEl.classList.remove('option--unavailable');
          }
        }
      });
    });
  }

  findVariantPartNumber(selection) {
    // Find a Part Number (SKU) that matches all the given selections
    for (const [sku, labels] of Object.entries(this.variantLabels)) {
      let matches = true;
      for (const [optName, optValue] of Object.entries(selection)) {
        if (labels[optName] !== optValue) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return sku;
      }
    }
    return null;
  }

  /**
   * Check if a Part Number exists for this product (not on another split product)
   */
  isPartNumberOnCurrentProduct(partNumber) {
    // If it's in optionValueIds, it means Shopify has this variant on the current product
    return !!this.optionValueIds[partNumber];
  }

  /**
   * Fetch variant data and specifications using Section Rendering API
   * This supports products with 250+ variants by loading on-demand
   * @returns {Object} { variant, specifications }
   */
  async fetchVariantByPartNumber(partNumber) {
    // Return from cache if available
    if (this.variantCache[partNumber]) {
      return {
        variant: this.variantCache[partNumber],
        specifications: this.specsCache[partNumber] || {}
      };
    }

    const optionValueId = this.optionValueIds[partNumber];
    if (!optionValueId) {
      // Part number not on this product - might be on a split product
      return { variant: null, specifications: {} };
    }

    try {
      // Use Section Rendering API with option_values parameter
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('option_values', optionValueId);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Failed to fetch variant: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      let variant = null;
      let specifications = {};

      // Extract variant data from the rendered section's JSON script
      const variantScript = doc.querySelector('[data-gamma-initial-variant]');
      if (variantScript) {
        variant = JSON.parse(variantScript.textContent);
        if (variant.id) {
          this.variantCache[partNumber] = variant;
        }
      }

      // Extract specifications from the rendered section's JSON script
      const specsScript = doc.querySelector('[data-gamma-current-specs]');
      if (specsScript) {
        try {
          specifications = JSON.parse(specsScript.textContent);
          if (specifications && Object.keys(specifications).length > 0) {
            this.specsCache[partNumber] = specifications;
          }
        } catch (e) {
          console.warn('GammaConfigurator: Could not parse specs from response', e);
        }
      }

      return { variant, specifications };
    } catch (error) {
      console.error('GammaConfigurator: Error fetching variant', error);
      return { variant: null, specifications: {} };
    }
  }

  async switchVariant() {
    const partNumber = this.findVariantPartNumber(this.selections);

    if (!partNumber) {
      this.updatePartNumberDisplay('-');
      return;
    }

    // Check if this part number belongs to a different split product
    if (this.isSplitProduct && this.partNumberMap[partNumber]) {
      const targetHandle = this.partNumberMap[partNumber];
      const currentHandle = this.productHandle || this.extractHandleFromUrl();

      if (currentHandle && targetHandle !== currentHandle) {
        // Redirect to the other split product
        this.classList.add('gamma-configurator--redirecting');
        const url = new URL(`/products/${targetHandle}`, window.location.origin);
        url.searchParams.set('variant_sku', partNumber);
        window.location.href = url.toString();
        return;
      }
    }

    // Check if this variant is on the current product
    if (!this.isPartNumberOnCurrentProduct(partNumber)) {
      // Part number exists in variant_labels but not on this product
      // This could happen with split products - try to redirect
      if (this.isSplitProduct) {
        const targetHandle = this.partNumberMap[partNumber];
        if (targetHandle) {
          this.classList.add('gamma-configurator--redirecting');
          const url = new URL(`/products/${targetHandle}`, window.location.origin);
          url.searchParams.set('variant_sku', partNumber);
          window.location.href = url.toString();
          return;
        }
      }
      console.warn(`GammaConfigurator: Part number ${partNumber} not found on this product`);
      return;
    }

    // Prevent concurrent fetches
    if (this.isFetching) return;

    // Show loading state
    this.isFetching = true;
    this.classList.add('gamma-configurator--loading');

    try {
      // Fetch variant and specifications (will use cache if available)
      const { variant, specifications } = await this.fetchVariantByPartNumber(partNumber);

      if (variant) {
        this.currentVariant = variant;

        // Update form inputs
        this.updateFormVariantId(variant.id);

        // Update Part Number display
        this.updatePartNumberDisplay(partNumber);

        // Update URL
        this.updateURL(variant.id);

        // Publish variant change event for Dawn theme integration and other components
        // Include specifications so gamma-specifications can update without additional API calls
        this.publishVariantChange(variant, specifications);
      }
    } finally {
      this.isFetching = false;
      this.classList.remove('gamma-configurator--loading');
    }
  }

  extractHandleFromUrl() {
    // Extract product handle from URL path like /products/handle or /products/handle?...
    const match = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  updateFormVariantId(variantId) {
    // Update all variant ID inputs on the page
    document.querySelectorAll(`input[name="id"]`).forEach(input => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePartNumberDisplay(sku) {
    const partNumberEl = this.querySelector('[data-part-number]');
    if (partNumberEl) {
      partNumberEl.textContent = sku;
    }
  }

  updateURL(variantId) {
    if (!history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());

    // Update share button if exists
    const shareButton = document.querySelector('share-button');
    if (shareButton && typeof shareButton.updateUrl === 'function') {
      shareButton.updateUrl(url.toString());
    }
  }

  publishVariantChange(variant, specifications = {}) {
    // Integrate with Dawn's pub/sub system if available
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          variant: variant,
        }
      });
    }

    // Dispatch custom event for other components (like gamma-specifications)
    // Include specifications so specs component doesn't need to make another API call
    this.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: {
        variant,
        specifications
      }
    }));

    // Update price display manually if pub/sub doesn't handle it
    this.updatePriceDisplay(variant);
  }

  updatePriceDisplay(variant) {
    if (!variant) return;

    // Try to update price elements using Dawn's patterns
    const priceContainer = document.querySelector(`#price-${this.sectionId}`);
    if (priceContainer && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);

      const regularPrice = priceContainer.querySelector('.price-item--regular');
      if (regularPrice) {
        regularPrice.textContent = formattedPrice;
      }
    }
  }

  formatMoney(cents) {
    // Use Shopify's formatMoney if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  selectInitialVariant() {
    // Check URL for initial variant
    const urlParams = new URLSearchParams(window.location.search);
    const variantSku = urlParams.get('variant_sku');

    let initialPartNumber = null;

    if (variantSku) {
      // Coming from a split product redirect - use the SKU from URL
      initialPartNumber = variantSku;
    } else if (this.currentVariant?.sku) {
      // Use the initial variant's SKU
      initialPartNumber = this.currentVariant.sku;
    }

    if (initialPartNumber) {
      // Find the labels for this variant and set the dropdowns
      const labels = this.variantLabels[initialPartNumber];
      if (labels) {
        this.selections = { ...labels };
        this.options.forEach(option => {
          const select = this.querySelector(`[data-option-name="${option.name}"]`);
          if (select && labels[option.name]) {
            select.value = labels[option.name];
          }
        });
      }
    }

    if (!initialPartNumber || !this.variantLabels[initialPartNumber]) {
      // Find first valid combination from variant_labels that's on this product
      const firstValidSelection = this.findFirstValidCombination();
      if (firstValidSelection) {
        this.selections = firstValidSelection.labels;
        this.options.forEach(option => {
          const select = this.querySelector(`[data-option-name="${option.name}"]`);
          if (select) {
            select.value = this.selections[option.name];
          }
        });
      } else {
        // Just select first values
        this.options.forEach(option => {
          const select = this.querySelector(`[data-option-name="${option.name}"]`);
          if (select && option.values.length > 0) {
            select.value = option.values[0];
            this.selections[option.name] = option.values[0];
          }
        });
      }
    }

    this.updateDisabledOptions();
    this.switchVariant();
  }

  findFirstValidCombination() {
    // Find the first Part Number that's on this product and has all option labels
    for (const [sku, labels] of Object.entries(this.variantLabels)) {
      if (Object.keys(labels).length === this.options.length) {
        // Check this variant exists on the current product (has an option_value_id)
        if (this.optionValueIds[sku]) {
          return { sku, labels: { ...labels } };
        }
      }
    }
    return null;
  }

  attachListeners() {
    // Listen for product info loaded event (Dawn theme)
    document.addEventListener('product-info:loaded', () => {
      // Re-initialize if needed after product info updates
    });

    // Listen for reset button click
    const resetButton = this.querySelector('[data-reset-configurator]');
    if (resetButton) {
      resetButton.addEventListener('click', () => this.resetToDefaults());
    }
  }

  storeDefaultSelections() {
    // Store the initial selections as defaults
    this.defaultSelections = { ...this.selections };
  }

  resetToDefaults() {
    if (!this.defaultSelections) return;

    // Reset all dropdowns to default values
    this.selections = { ...this.defaultSelections };

    this.options.forEach(option => {
      const select = this.querySelector(`[data-option-name="${option.name}"]`);
      if (select && this.defaultSelections[option.name]) {
        select.value = this.defaultSelections[option.name];
      }
    });

    // Update the variant
    this.updateDisabledOptions();
    this.switchVariant();
  }
}

// Register custom element
if (!customElements.get('gamma-configurator')) {
  customElements.define('gamma-configurator', GammaConfigurator);
}
